import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../../app/auth/auth.service';
import { CompanyContextService } from '../../company-context.service';
import { ApiResponse } from '../api-response.model';

export interface FinancialYear {
  id?: number;
  financialYearId?: number;
  name: string;
  startDate: string;
  endDate: string;
  isDefault: boolean;
  isActive: boolean;
}

export interface FinancialYearRequest {
  name: string;
  startDate: string;
  endDate: string;
  isDefault: boolean;
  isActive: boolean;
}

type ApiResult<T> = ApiResponse<T> | T;

@Injectable({ providedIn: 'root' })
export class FinancialYearService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly companyContext = inject(CompanyContextService);
  private readonly apiUrl = environment.financialYearsApiUrl;

  getFinancialYears(activeOnly?: boolean): Observable<FinancialYear[]> {
    let params = new HttpParams();
    if (activeOnly !== undefined) params = params.set('activeOnly', activeOnly);

    return this.http
      .get<ApiResult<FinancialYear[]>>(this.apiUrl, { headers: this.companyHeaders(), params })
      .pipe(map(response => this.unwrap(response)));
  }

  getFinancialYear(id: number): Observable<FinancialYear> {
    return this.http
      .get<ApiResult<FinancialYear>>(`${this.apiUrl}/${id}`, { headers: this.companyHeaders() })
      .pipe(map(response => this.unwrap(response)));
  }

  createFinancialYear(request: FinancialYearRequest): Observable<FinancialYear> {
    return this.http
      .post<ApiResult<FinancialYear>>(this.apiUrl, request, { headers: this.companyHeaders() })
      .pipe(map(response => this.unwrap(response)));
  }

  updateFinancialYear(id: number, request: FinancialYearRequest): Observable<FinancialYear> {
    return this.http
      .put<ApiResult<FinancialYear>>(`${this.apiUrl}/${id}`, request, { headers: this.companyHeaders() })
      .pipe(map(response => this.unwrap(response)));
  }

  deleteFinancialYear(id: number): Observable<void> {
    return this.http
      .delete<ApiResult<void>>(`${this.apiUrl}/${id}`, { headers: this.companyHeaders() })
      .pipe(map(response => this.unwrap(response)));
  }

  private companyHeaders(): HttpHeaders {
    const user = this.auth.session()?.user;
    if (!user) return new HttpHeaders();

    const companyId = user.isSuperAdmin
      ? this.companyContext.getSelectedCompanyId(user.id) ?? user.compId
      : user.compId;

    return companyId == null
      ? new HttpHeaders()
      : new HttpHeaders({ 'X-Company-Id': String(companyId) });
  }

  private unwrap<T>(response: ApiResult<T>): T {
    if (response && typeof response === 'object' && 'data' in response) {
      return (response as ApiResponse<T>).data;
    }
    return response as T;
  }
}
